import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import type { Event2Label } from "@wedding/shared";

interface WeddingInfo {
  slug: string;
  title: string;
  date_time: string | null;
  location: string | null;
  description: string | null;
  /** Ana etkinliğin türü — null ise UI "Düğün" yazar (migration 051). */
  event1_label: Event2Label | null;
  event1_label_custom: string | null;
  /** Opsiyonel ikinci etkinlik — ana tarihten önce de sonra da olabilir. */
  event2_date_time: string | null;
  event2_location: string | null;
  event2_label: Event2Label | null;
  event2_label_custom: string | null;
  config: Record<string, unknown>;
bg_auth: string | null;
  bg_countdown: string | null;
  bg_rsvp: string | null;
  join_cover_images: string[];
  join_frame_count: number;
  cover_photo_url: string | null;
  /**
   * Effective (runtime-configurable) media size limits from the server. Used
   * for advisory client-side pre-upload validation; the server re-checks and is
   * authoritative. Optional — older API responses omit it (client falls back to
   * the @wedding/shared compile-time constants).
   */
  limits?: {
    maxImageBytes: number;
    maxVideoDirectBytes: number;
  };
}

interface WeddingContextValue {
  wedding: WeddingInfo | null;
  isLoading: boolean;
  error: string | null;
  slug: string;
}

const WeddingContext = createContext<WeddingContextValue | null>(null);

export function WeddingProvider({ children }: { children: React.ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const [wedding, setWedding] = useState<WeddingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setError("Wedding not found");
      setIsLoading(false);
      return;
    }

    // Always use relative paths — Vite proxy handles dev, CF custom domain handles prod
    const base = `/api/w/${slug}/info`;

    fetch(base, { credentials: "include" })
      .then((res) => res.json())
      .then((data: { ok: boolean; data?: WeddingInfo; error?: string }) => {
        if (data.ok && data.data) {
          setWedding({ ...data.data, slug: slug });
        } else {
          setError(data.error ?? "Wedding not found");
        }
      })
      .catch(() => setError("Failed to load wedding"))
      .finally(() => setIsLoading(false));
  }, [slug]);

  return (
    <WeddingContext.Provider value={{ wedding, isLoading, error, slug: slug ?? "" }}>
      {children}
    </WeddingContext.Provider>
  );
}

export function useWedding() {
  const ctx = useContext(WeddingContext);
  if (!ctx) throw new Error("useWedding must be used within WeddingProvider");
  return ctx;
}
